import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Hook to track unread message count with polling.
 * Polls /api/messages/unread-count every 30 seconds.
 * Shows a toast when the count increases (new message arrived).
 *
 * Returns the current unread count.
 */
export const useUnreadMessages = (pollIntervalMs = 30000) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const previousCountRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const fetchCount = async () => {
      try {
        const res = await axios.get(`${API}/messages/unread-count`);
        if (cancelled) return;
        const newCount = res.data?.unread_count ?? 0;
        const prev = previousCountRef.current;
        // Notify only when count grew compared to a previous fetch (not initial load)
        if (prev !== null && newCount > prev) {
          const diff = newCount - prev;
          toast.info(
            diff === 1 ? "Sie haben eine neue Nachricht" : `Sie haben ${diff} neue Nachrichten`,
            { duration: 6000 }
          );
        }
        previousCountRef.current = newCount;
        setUnreadCount(newCount);
      } catch (err) {
        // Silent: 401 just means user is not logged in
      }
    };

    fetchCount();
    const interval = setInterval(fetchCount, pollIntervalMs);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [pollIntervalMs]);

  return unreadCount;
};
